import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WelcomePopup from "@/components/WelcomePopup";
import SafeEmail from "@/components/SafeEmail";
import ReferralCallout from "@/components/ReferralCallout";
import { useT } from "@/context/LangContext";
import { useBrand } from "@/context/BrandContext";
import {
  ArrowRight,
  Users,
  Eye,
  Heart,
  Handshake,
  ClipboardCheck,
  BadgeCheck,
  Sparkles,
  Check,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";

export default function MerchantLandingPage() {
  const { t, lang } = useT();
  const { brand } = useBrand();
  const heroSubtitle = (lang === "nl" ? brand.hero_subtitle_nl : brand.hero_subtitle_fr) || t("ma.hero.subtitle");

  return (
    <div className="App">
      <WelcomePopup disabled />
      <Header />

      {/* HERO */}
      <section 
        className="relative bg-white" 
        style={{ 
          paddingTop: '6rem',
          paddingBottom: '3rem',
          isolation: 'isolate',
          contain: 'layout',
          zIndex: 1,
        }}
        data-testid="ma-hero"
      >
        <div className="container-app">
          <div style={{ maxWidth: '100%', overflow: 'visible' }}>
            <h1 
              style={{
                fontSize: '28px',
                lineHeight: '1.2',
                fontWeight: 700,
                color: '#0b1220',
                fontFamily: '-apple-system, BlinkMacSystemFont, Helvetica, Arial, sans-serif',
                margin: 0,
                padding: 0,
                textShadow: 'none',
                background: 'transparent',
                position: 'relative',
                zIndex: 2,
                wordBreak: 'normal',
                overflowWrap: 'break-word',
              }}
              data-testid="ma-hero-title"
            >
              <div style={{ display: 'block', color: '#0b1220' }}>
                {t("ma.hero.title.before")}
              </div>
              <div style={{ display: 'block', color: '#65a30d', marginTop: '4px' }}>
                {t("ma.hero.title.brand")}
              </div>
            </h1>

            <p className="mt-6 text-lg md:text-xl text-navy-900/70 max-w-2xl leading-relaxed">
              {heroSubtitle}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/devenir-partenaire"
                className="inline-flex items-center gap-2 bg-lime-500 hover:bg-lime-400 text-navy-900 font-bold px-7 py-4 rounded-full transition text-base shadow-lg shadow-lime-500/30"
                data-testid="ma-hero-cta-primary"
              >
                {t("ma.hero.cta_primary")} <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="#how"
                className="inline-flex items-center gap-2 bg-navy-900/5 hover:bg-navy-900/10 text-navy-900 font-semibold px-7 py-4 rounded-full transition text-base"
                data-testid="ma-hero-cta-secondary"
              >
                {t("ma.hero.cta_secondary")}
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-navy-900/60">
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-lime-600" /> {t("ma.hero.trust_1")}</span>
              <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-lime-600" /> {t("ma.hero.trust_2")}</span>
              <span className="inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-lime-600" /> {t("ma.hero.trust_3")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* WHY JOIN */}
      <section id="why" className="section-pad bg-white" data-testid="ma-why">
        <div className="container-app">
          <div className="max-w-2xl mb-12">
            <div className="inline-block chip bg-lime-500/15 text-navy-900 mb-3">{t("ma.why.badge")}</div>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl text-navy-900 tracking-tight">
              {t("ma.why.title")}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <WhyCard icon={Users} title={t("ma.why.1.title")} desc={t("ma.why.1.desc")} />
            <WhyCard icon={Eye} title={t("ma.why.2.title")} desc={t("ma.why.2.desc")} />
            <WhyCard icon={BadgeCheck} title={t("ma.why.3.title")} desc={t("ma.why.3.desc")} />
            <WhyCard icon={Heart} title={t("ma.why.4.title")} desc={t("ma.why.4.desc")} />
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="section-pad bg-navy-900 text-white relative overflow-hidden" data-testid="ma-how">
        <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] bg-lime-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="container-app relative">
          <div className="max-w-2xl mb-14">
            <div className="inline-block chip bg-lime-500 text-navy-900 mb-3">{t("ma.how.badge")}</div>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight">
              {t("ma.how.title")}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-10 relative">
            <HowStep n={1} icon={Handshake} title={t("ma.how.1.title")} desc={t("ma.how.1.desc")} />
            <HowStep n={2} icon={ClipboardCheck} title={t("ma.how.2.title")} desc={t("ma.how.2.desc")} />
            <HowStep n={3} icon={Sparkles} title={t("ma.how.3.title")} desc={t("ma.how.3.desc")} />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="section-pad bg-white" data-testid="ma-pricing">
        <div className="container-app">
          <div className="max-w-2xl mb-12">
            <div className="inline-block chip bg-lime-500/15 text-navy-900 mb-3">{t("ma.pricing.badge")}</div>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl text-navy-900 tracking-tight">
              {t("ma.pricing.title")}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* FREE */}
            <div className="card-premium p-8 relative border-2 border-lime-500/50" data-testid="ma-pricing-free">
              <div className="absolute top-5 right-5 chip bg-lime-500 text-navy-900 font-bold">★ Recommandé</div>
              <div className="font-heading text-sm uppercase tracking-widest text-navy-900/60 font-bold mb-2">
                {t("ma.pricing.free.title")}
              </div>
              <div className="font-heading text-6xl font-black text-navy-900 leading-none mb-3">
                {t("ma.pricing.free.price")}
              </div>
              <p className="text-navy-900/70 text-base leading-relaxed mb-5">
                {t("ma.pricing.free.desc")}
              </p>
              <ul className="space-y-2">
                {t("ma.pricing.free.bullets").split(" · ").map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-navy-900/80">
                    <Check className="w-4 h-4 text-lime-600 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <Link
                to="/devenir-partenaire"
                className="mt-6 inline-flex items-center gap-2 bg-navy-900 text-lime-400 hover:bg-navy-900/90 font-bold px-6 py-3 rounded-full transition w-fit"
                data-testid="ma-pricing-free-cta"
              >
                {t("ma.hero.cta_primary")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* DONE FOR YOU */}
            <div className="card-premium p-8 bg-navy-900 text-white" data-testid="ma-pricing-done">
              <div className="font-heading text-sm uppercase tracking-widest text-lime-400 font-bold mb-2">
                {t("ma.pricing.done.title")}
              </div>
              <div className="font-heading text-6xl font-black leading-none mb-3">
                {t("ma.pricing.done.price")}
              </div>
              <p className="text-white/70 text-base leading-relaxed mb-5">
                {t("ma.pricing.done.desc")}
              </p>
              <ul className="space-y-2">
                {t("ma.pricing.done.bullets").split(" · ").map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-white/80">
                    <Check className="w-4 h-4 text-lime-400 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <SafeEmail
                user="sante"
                domain="bhealth.be"
                subject='Service "On le fait pour vous" - Local Benefits'
                icon
                className="mt-6 inline-flex items-center gap-2 bg-lime-500 hover:bg-lime-400 text-navy-900 font-bold px-6 py-3 rounded-full transition w-fit"
                testId="ma-pricing-done-cta"
              >
                Nous contacter
              </SafeEmail>
            </div>
          </div>
        </div>
      </section>

      {/* REFERRAL — Network advantage as commercial argument for the merchant */}
      <ReferralCallout variant="b2b" />

      {/* FINAL CTA */}
      <section className="section-pad bg-gradient-to-br from-lime-400 to-lime-500" data-testid="ma-final-cta">
        <div className="container-app text-center max-w-3xl mx-auto">
          <div className="inline-block chip bg-navy-900 text-lime-400 mb-4">{t("ma.final.kicker")}</div>
          <h2 className="font-heading font-black text-navy-900 text-4xl md:text-6xl tracking-tighter leading-tight">
            {t("ma.final.title")}
          </h2>
          <p className="text-navy-900/80 text-lg mt-4">{t("ma.final.desc")}</p>

          <Link
            to="/devenir-partenaire"
            className="mt-10 inline-flex items-center gap-2 bg-navy-900 text-lime-400 hover:bg-navy-900/90 font-bold px-8 py-5 rounded-full transition text-lg shadow-2xl"
            data-testid="ma-final-cta-btn"
          >
            {t("ma.final.cta")} <ArrowRight className="w-5 h-5" />
          </Link>

          <div className="mt-12 text-sm text-navy-900/70">
            <div className="mb-2 font-semibold">{t("ma.final.contact_label")}</div>
            <div className="flex flex-wrap justify-center gap-6">
              {(() => { const [u, d] = (brand.email || "").split("@"); return u && d ? <SafeEmail user={u} domain={d} icon className="inline-flex items-center gap-2 hover:text-navy-900" testId="ma-final-email" /> : null; })()}
              {brand.phone && (
                <a href={`tel:${brand.phone.replace(/\s/g,"")}`} className="inline-flex items-center gap-2 hover:text-navy-900">
                  <Phone className="w-4 h-4" /> {brand.phone}
                </a>
              )}
              <span className="inline-flex items-center gap-2">
                <MapPin className="w-4 h-4" /> {brand.city}
              </span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function WhyCard({ icon: Icon, title, desc }) {
  return (
    <div className="card-premium p-6 hover:-translate-y-1 transition-transform">
      <div className="w-12 h-12 rounded-xl bg-lime-500/20 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-lime-700" />
      </div>
      <h3 className="font-heading font-bold text-navy-900 text-lg mb-2">{title}</h3>
      <p className="text-sm text-navy-900/70 leading-relaxed">{desc}</p>
    </div>
  );
}

function HowStep({ n, icon: Icon, title, desc }) {
  return (
    <div className="relative">
      <div className="flex items-center gap-4 mb-4">
        <div className="font-heading font-black text-6xl text-lime-400 leading-none">{n}</div>
        <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
          <Icon className="w-5 h-5 text-lime-400" />
        </div>
      </div>
      <h3 className="font-heading font-bold text-white text-xl mb-2">{title}</h3>
      <p className="text-sm text-white/70 leading-relaxed">{desc}</p>
    </div>
  );
}
